import React, { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmtDate, voucherRef } from '../utils';
import {
  Card, CardHeader, IssueBadge, Badge, Tag,
  SearchInput, Pill, Skeleton, PageWrap, PageHeader, Table, useIsMobile,
} from '../components/UI';
import '../styles/AuditIssues.css';

export default function AuditIssues({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.auditSummary(sourceId, null, syncRunId), [sourceId, syncRunId]);
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const isMobile = useIsMobile();

  const issues = data?.issues || [];
  const types = useMemo(() =>
    [...new Set(issues.map(i => i.issue_type).filter(Boolean))],
    [issues]
  );
  const filtered = useMemo(() => {
    let rows = issues;
    if (typeFilter) rows = rows.filter(i => i.issue_type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(i => JSON.stringify(i).toLowerCase().includes(q));
    }
    return rows;
  }, [issues, typeFilter, search]);

  return (
    <PageWrap>
      <PageHeader title="Audit Issues" sub="All flagged issues from the latest sync" />
      <Card>
        <CardHeader
          title={`${filtered.length} Issues${typeFilter ? ` · ${typeFilter.replace(/_/g,' ')}` : ''}`}
          right={<SearchInput value={search} onChange={setSearch} placeholder="Search issues..." />}
        />

        <div className="ai-pills">
          <Pill label={`All (${issues.length})`} active={!typeFilter} onClick={() => setTypeFilter('')} />
          {types.map(t => (
            <Pill
              key={t}
              label={`${t.replace(/_/g,' ')} (${issues.filter(i => i.issue_type === t).length})`}
              active={typeFilter === t}
              onClick={() => setTypeFilter(typeFilter === t ? '' : t)}
            />
          ))}
        </div>

        <div className="ai-table-wrap">
          {loading ? <Skeleton rows={6} /> : (
            <Table
              columns={[
                { label: 'Type', key: 'issue_type',
                  render: r => <IssueBadge type={r.issue_type} /> },
                { label: 'Voucher', key: 'voucher_ref', noWrap: true,
                  render: r => {
                    const ref = voucherRef(r);
                    return ref ? <Tag>{ref}</Tag> : <span className="ai-dim">—</span>;
                  }},
                { label: 'Date', key: 'date', noWrap: true,
                  render: r => <span className="ai-date">{fmtDate(r.date)}</span> },
                { label: 'Party / Ledger', key: 'party', maxWidth: 160,
                  render: r => (
                    <span className="ai-sm">
                      {r.party || r.ledger_name || <span className="ai-dim">—</span>}
                    </span>
                  )},
                { label: 'Message', key: 'message', maxWidth: 280,
                  render: r => <span className="ai-text">{r.message}</span> },
                { label: 'Status', key: 'resolved',
                  render: r => <Badge text={r.resolved ? 'Resolved' : 'Open'} variant={r.resolved ? 'green' : 'amber'} /> },
              ]}
              rows={filtered}
              emptyMsg="No issues matching filter"
            />
          )}
        </div>
      </Card>
    </PageWrap>
  );
}
